import React, { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const SIZE_CLASS: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

interface UiModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: IconDefinition;
  /** Tailwind color class for icon, e.g. "text-red-500" */
  iconColor?: string;
  /** Inline style for icon — use when CSS variable colors are needed */
  iconStyle?: React.CSSProperties;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Enables max-h-[90vh] overflow-y-auto with sticky header */
  scrollable?: boolean;
  /** Set false to keep the modal open on backdrop click (forms with in-progress input). Default true. */
  closeOnBackdrop?: boolean;
  children: ReactNode;
  /** Rendered below the scrollable body (action buttons, etc.) */
  footer?: ReactNode;
}

const UiModal: React.FC<UiModalProps> = ({
  open,
  onClose,
  title,
  icon,
  iconColor = '',
  iconStyle,
  size = 'md',
  scrollable = false,
  closeOnBackdrop = true,
  children,
  footer,
}) => {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        className={`bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full ${SIZE_CLASS[size] ?? SIZE_CLASS.md} ${
          scrollable ? 'max-h-[90vh] overflow-y-auto' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`${
            scrollable ? 'sticky top-0 z-10' : ''
          } bg-white dark:bg-slate-800 p-5 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between rounded-t-2xl`}
        >
          <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            {icon && (
              <FontAwesomeIcon icon={icon} className={iconColor} style={iconStyle as any} />
            )}
            {title}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="p-5 space-y-4 text-gray-700 dark:text-slate-300">{children}</div>

        {footer && <div className="px-5 pb-5">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default UiModal;
